import * as React from "react";
import {FrameworkForm, type FrameworkFormData} from "components/admin/frameworks/framework-form";
import useApi from "hooks/use-api";
import {useCallback, useEffect, useState} from "react";
import type Framework from "benchmarking-domain/framework";
import {paths} from "paths";
import {enqueueSnackbar} from "notistack";
import {useNavigate, useParams} from "react-router-dom";
import {QuestionnaireForm} from "../../../components/admin/frameworks/questionnaire-form";
import {KpiStructureForm} from "../../../components/admin/frameworks/kpi-structure-form";
import Stack from "@mui/material/Stack";
import Questionnaire from "benchmarking-domain/questionnaire";
import {KpiStructure} from "benchmarking-domain/kpi-definition";
import {DynamicQuestionnaire} from "benchmarking-domain/dynamic-questionnaire";
import {DynamicQuestionnaireForm} from "../../../components/admin/frameworks/dynamic-questionnaire-form";
import {NormalizationFactors} from "benchmarking-domain/normalization-factors";
import { NormalizationFactorsForm } from "components/admin/frameworks/normalization-factors-form";
import { ProductionNormalizationFactorsForm } from "components/admin/frameworks/production-normalization-factors-form";

const FrameworkFormMemo = React.memo(FrameworkForm);
const NormalizationFactorsFormMemo = React.memo(NormalizationFactorsForm);
const QuestionnaireFormMemo = React.memo(QuestionnaireForm);
const DynamicQuestionnaireFormMemo = React.memo(DynamicQuestionnaireForm);
const KpiStructureFormMemo = React.memo(KpiStructureForm);

export default function FrameworkDetails(): React.JSX.Element {

  const { frameworkId } = useParams();
  const navigate = useNavigate();
  const { subscribeFramework, saveFramework, subscribeQuestionnaires, saveQuestionnaire, subscribeDynamicQuestionnaires, saveDynamicQuestionnaire, subscribeCurrentKpiStructure, saveKpiStructure, subscribeNormalizationFactors, subscribeProductionNormalizationFactors, saveNormalizationFactors, saveProductionNormalizationFactors } = useApi();
  const [framework, setFramework] = useState<Framework | undefined>();
  const [normalizationFactors, setNormalizationFactors] = useState<NormalizationFactors | undefined>();
  const [productionNormalizationFactors, setProductionNormalizationFactors] = useState<NormalizationFactors | undefined>();
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[] | undefined>();
  const [dynamicQuestionnaires, setDynamicQuestionnaires] = useState<DynamicQuestionnaire[] | undefined>();
  const [kpiStructure, setKpiStructure] = useState<KpiStructure | undefined>();

  useEffect(() => {
    if (!frameworkId) {
      navigate(paths.errors.notFound, { replace: true});
      return;
    }

    const unsubscribeFramework = subscribeFramework(frameworkId, (framework) => framework ? setFramework(framework) : navigate(paths.errors.notFound, { replace: true }));
    const unsubscribeNormalizationFactors = subscribeNormalizationFactors(frameworkId, setNormalizationFactors);
    const unsubscribeProductionNormalizationFactors = subscribeProductionNormalizationFactors(frameworkId, setProductionNormalizationFactors);
    const unsubscribeQuestionnaires = subscribeQuestionnaires(frameworkId, false, setQuestionnaires);
    const unsubscribeDynamicQuestionnaires = subscribeDynamicQuestionnaires(frameworkId, false, setDynamicQuestionnaires);
    const unsubscribeCurrentKpiStructure = subscribeCurrentKpiStructure(frameworkId, setKpiStructure);

    return () => {
      unsubscribeFramework();
      unsubscribeNormalizationFactors();
      unsubscribeProductionNormalizationFactors();
      unsubscribeQuestionnaires();
      unsubscribeDynamicQuestionnaires();
      unsubscribeCurrentKpiStructure();
    }
  }, [navigate, frameworkId, subscribeFramework, subscribeQuestionnaires, subscribeCurrentKpiStructure, subscribeDynamicQuestionnaires, subscribeNormalizationFactors, subscribeProductionNormalizationFactors]);

  const submitFrameworkChanges = useCallback(async (data: FrameworkFormData) => {
    try {
      await saveFramework({...data, id: frameworkId!});
      enqueueSnackbar('Framework changed!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Framework save failed! ' + error, { variant: 'error' });
    }

  }, [frameworkId, saveFramework]);

  const submitNormalizationFactors = useCallback(async (data: NormalizationFactors) => {
    try {
      await saveNormalizationFactors(frameworkId!, data);
      enqueueSnackbar('Normalization factors changed!', { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar('Normalization factors save failed! ' + error, { variant: 'error' });
    }
  }, [frameworkId, saveNormalizationFactors]);

  const submitProductionNormalizationFactors = useCallback(async (data: any) => {
    try {
      await saveProductionNormalizationFactors(frameworkId!, data);
      enqueueSnackbar('Normalization factors changed!', { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar('Normalization factors save failed! ' + error, { variant: 'error' });
    }
  }, [frameworkId, saveProductionNormalizationFactors]);

  const submitQuestionnaire = useCallback(async (data: Questionnaire) => {
    try {
      await saveQuestionnaire(frameworkId!, data);
      enqueueSnackbar('Questionnaire changed!', { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar('Questionnaire save failed! ' + error, { variant: 'error' });
    }
  }, [frameworkId, saveQuestionnaire]);

  const submitDynamicQuestionnaire = useCallback(async (data: DynamicQuestionnaire) => {
    try {
      await saveDynamicQuestionnaire(frameworkId!, data);
      enqueueSnackbar('Dynamic questionnaire changed!', { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar('Dynamic questionnaire save failed! ' + error, { variant: 'error' });
    }
  }, [frameworkId, saveDynamicQuestionnaire]);

  const submitKpiStructure = useCallback(async (data: KpiStructure) => {
    try {
      await saveKpiStructure(frameworkId!, data);
      enqueueSnackbar('KPI structure changed!', { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar('KPI structure save failed! ' + error, { variant: 'error' });
    }
    }, [frameworkId, saveKpiStructure]);



  if(!framework)
    return <>Loading...</>;

  return <Stack spacing={3}>
    <FrameworkFormMemo framework={framework} onSubmit={submitFrameworkChanges} />
    <NormalizationFactorsForm headerTitle="Normalization factors" normalizationFactors={normalizationFactors} onSubmit={submitNormalizationFactors} />
    <ProductionNormalizationFactorsForm headerTitle="Production normalization factors" normalizationFactors={productionNormalizationFactors} onSubmit={submitProductionNormalizationFactors} />
    {questionnaires && questionnaires.length > 0 && questionnaires.map(questionnaire => {
      return <QuestionnaireFormMemo key={questionnaire.id} questionnaire={questionnaire} onSubmit={submitQuestionnaire} />;
      })}
    {(!questionnaires || questionnaires.length === 0) &&
      <QuestionnaireFormMemo onSubmit={submitQuestionnaire} />
    }
    {dynamicQuestionnaires && dynamicQuestionnaires.length > 0 && dynamicQuestionnaires.map(questionnaire => {
      return <DynamicQuestionnaireFormMemo key={questionnaire.id} dynamicQuestionnaire={questionnaire} onSubmit={submitDynamicQuestionnaire} />;
    })}
    {(!dynamicQuestionnaires || dynamicQuestionnaires.length === 0) &&
      <DynamicQuestionnaireFormMemo onSubmit={submitDynamicQuestionnaire} />
    }
    { kpiStructure && (
        <KpiStructureFormMemo key={kpiStructure.id} kpiStructure={kpiStructure} onSubmit={submitKpiStructure} />
      )
    }

  </Stack>;
}
